import React, { useEffect, useState } from "react";
import DashboardAppointmentList from "./DashboardAppointmentList";
import Table from "react-bootstrap/Table";
import { useNavigate } from "react-router-dom";
import { Alert, InputAdornment, Skeleton, TableCell, TextField, MenuItem } from "@mui/material";
import SearchRounded from "@mui/icons-material/SearchRounded";
import { Button } from "../../../common/Button";
import SchedulingPageControls from "../SchedulingPageControls";
import useSchedulingPage from "../../../../hooks/ReactQueryHooks/useSchedulingPage";
import { listAppointments } from "../../../api/Scheduling/appointments";
import { useServiceCategoriesQuery } from "../../../../hooks/ReactQueryHooks/useServiceCategoriesQuery";
import { formatRange } from "../time";
import { loadPatientInfo } from "../names";
import { appointmentStatusLabel, subjectPatientRef } from "./statusLabels";
import { canLaunchAppointment, launchOrOpenAppointment } from "./launchAppointmentSession";
import AppointmentDetailModal from "./AppointmentDetailModal";
import { useSchedulingOrg } from "../../../../hooks/ReactQueryHooks/useSchedulingOrg";
import { useSchedulingTimeZone } from "../../../../hooks/useSchedulingTimeZone";
import { failed } from "../../../common/Toastify";
import { schedulingErrorMessage } from "../../../api/Scheduling/errors";
import TimeZoneSelect from "../TimeZoneSelect";
import "./DashboardAppointments.css";

const AppointmentsList = ({
  organizationRef,
  practitionerRef,
  patientRef,
  status,
  startsAt,
  endsAt,
  readOnly = false,
  scope: scopeOverride,
  isProviderAppointment = false,
  dashboard = false,
  dashboardTab,
}) => {
  const navigate = useNavigate();
  const { user, organizationTimeZone, excludedOrganizationRefs, practitionerRef: sessionPractitionerRef } = useSchedulingOrg();
  const displayZone = useSchedulingTimeZone(user);
  const [nameSearch, setNameSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(status || "all");
  const [selected, setSelected] = useState(null);
  const [names, setNames] = useState({});
  const [identifiers, setIdentifiers] = useState({});
  const [launchingId, setLaunchingId] = useState("");
  const rowsPerPage = 10;
  useEffect(() => { setNameSearch(""); setSelected(null); }, [organizationRef, practitionerRef, excludedOrganizationRefs, dashboardTab, statusFilter]);

  const query = useSchedulingPage("scheduling-appointments", {
    organizationRef,
    scope: scopeOverride || (dashboard ? "descendants" : "own"), excludedOrganizationRefs,
    practitionerRef,
    patientRef,
    status: dashboardTab ? status : statusFilter === "all" ? undefined : statusFilter,
    startsAt,
    endsAt,
  }, listAppointments, "appointments", { enabled: !dashboard || !!practitionerRef });
  const { data, isLoading, isError, refetch } = query;
  const queryText = nameSearch.trim().toLowerCase();
  const filtered = !queryText
    ? data
    : data.filter((row) => {
        const ref = subjectPatientRef(row);
        return `${names[ref] || ""} ${ref || ""}`.toLowerCase().includes(queryText);
      });
  const searchField = (
    <TextField
      className="appointment-patient-search"
      size="small"
      placeholder="Search patient name"
      value={nameSearch}
      onChange={(event) => setNameSearch(event.target.value)}
      inputProps={{ "aria-label": "Search patient name" }}
      InputProps={{ startAdornment: <InputAdornment position="start"><SearchRounded fontSize="small" /></InputAdornment> }}
    />
  );
  const { data: categories = [] } = useServiceCategoriesQuery({ scope: scopeOverride || (dashboard ? "descendants" : "own"), excludedOrganizationRefs, organizationRef, active: undefined });

  useEffect(() => {
    data.forEach((row) => {
      const ref = subjectPatientRef(row);
      if (ref && !names[ref]) {
        loadPatientInfo(ref).then((info) => {
          setNames((prev) => ({ ...prev, [ref]: info.name }));
          setIdentifiers((prev) => ({ ...prev, [ref]: info.identifier }));
        });
      }
    });
  }, [data]);

  const handleLaunch = async (row) => {
    if (launchingId || !canLaunchAppointment({ appointment: row, practitionerRef: sessionPractitionerRef, readOnly, isProviderAppointment })) return;
    setLaunchingId(row.appointmentId);
    try {
      await launchOrOpenAppointment({
        appointment: row,
        organizationRef,
        practitionerRef: sessionPractitionerRef,
        patientName: names[subjectPatientRef(row)],
        navigate,
        readOnly,
        isProviderAppointment,
      });
    } catch (error) {
      failed(schedulingErrorMessage(error));
    } finally {
      setLaunchingId("");
    }
  };

  return (
    <>
      {dashboard ? <DashboardAppointmentList rows={filtered} names={names} identifiers={identifiers} categories={categories}
        isLoading={isLoading} isError={isError} onRetry={refetch}
        search={nameSearch} onSearchChange={setNameSearch} searchControl={searchField} paged
        displayZone={displayZone} organizationTimeZone={organizationTimeZone}
        tab={dashboardTab} status={statusFilter} onStatusChange={setStatusFilter}
        page={0} rowsPerPage={rowsPerPage} onPageChange={() => {}}
        onSelect={setSelected} onLaunch={handleLaunch} launchingId={launchingId}
        canLaunch={(row) => canLaunchAppointment({ appointment: row, practitionerRef: sessionPractitionerRef, readOnly, isProviderAppointment })} /> : <div className="appointments-page-list appointment-list-panel">
      <div className="appointment-list-toolbar">
        {searchField}
        <TextField select size="small" label="Status" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="proposed">Pending</MenuItem>
          <MenuItem value="booked">Accepted</MenuItem>
          <MenuItem value="cancelled">Rejected</MenuItem>
          <MenuItem value="fulfilled">Completed</MenuItem>
          <MenuItem value="no_show">No show</MenuItem>
        </TextField>
        <div className="appointment-time-zone">
          <TimeZoneSelect value={displayZone.preference} onChange={displayZone.setPreference}
            localZone={displayZone.deviceTimeZone} organizationZone={organizationTimeZone} />
        </div>
      </div>
      {isError && <Alert severity="error" action={<button onClick={refetch}>Retry</button>}>Could not load appointments.</Alert>}
      <div className="table-wrap">
        <Table responsive>
          <thead>
            <tr>
              <th>Patient</th>
              <th>Type</th>
              <th>Appointment Time</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              [1, 2, 3].map((val) => (
                <tr key={val}>
                  <td colSpan={5}>
                    <Skeleton animation="wave" />
                  </td>
                </tr>
              ))
            ) : isError ? null : filtered.length ? (
              filtered.map((row) => {
                const ref = subjectPatientRef(row);
                const categoryName =
                  categories.find((category) => category.serviceCategoryId === row.serviceCategoryId)?.name ||
                  row.serviceCategoryId;
                return (
                  <tr key={row.appointmentId} onClick={() => setSelected(row)} style={{ cursor: "pointer" }}>
                    <td>
                      <button
                        type="button"
                        className="btn btn-link p-0"
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate(`/app/patients-details?patientId=${ref}`);
                        }}
                      >
                        {names[ref] || ref}
                      </button>
                    </td>
                    <td>{categoryName}</td>
                    <td>{formatRange(row.startsAt, row.endsAt, displayZone.timeZone)}</td>
                    <td>
                      <span className={`scheduling-status ${row.status}`}>{appointmentStatusLabel(row.status)}</span>
                    </td>
                    <td>
                      <Button
                        variant="secondary"
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelected(row);
                        }}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <TableCell colSpan={5} style={{ textAlign: "center" }}>
                  {queryText ? "No matching appointments" : "No appointments"}
                </TableCell>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      </div>}
      <SchedulingPageControls query={query} label="appointments" />
      {selected ? (
        <AppointmentDetailModal
          timezoneName={displayZone.timeZone}
          appointment={selected}
          organizationRef={organizationRef}
          readOnly={readOnly}
          isProviderAppointment={isProviderAppointment}
          onHide={() => setSelected(null)}
          onChanged={() => {
            setSelected(null);
          }}
        />
      ) : null}
    </>
  );
};

export default AppointmentsList;
